import styled from 'styled-components'

/**
 * Usage allowance cards.
 *
 * These were full-width rows above a table, where an allowance was a passing
 * detail. On Request limits the allowance is the subject of the page, so each
 * one is a card and the number it turns on gets to be the card's primary metric.
 *
 * DESIGN.md compliance:
 *  - §11 `card-flat`: {colors.surface}, {rounded.lg} (20px), {spacing.lg}
 *    horizontal padding.
 *  - §05: no invented type sizes. The remaining count is {typography.stat-value}
 *    (§03's 30px, which a card's primary metric is exactly what it is for); the
 *    title is {typography.body-strong} (15/700) and supporting copy is
 *    {typography.caption} (13/500).
 *  - §11 `button-secondary` for "Request increase" — it is an action, so it gets
 *    a button on the approved height system, not an underlined link.
 *  - §02 "Status Is Never Color Alone": the count is spelled out and the pill
 *    carries a word in every tone, so nothing is signalled by hue alone.
 *  - §04: warning is {colors.status-inactive}, the non-alarming amber — running
 *    low is not a failure. Only a spent allowance uses {colors.error}. The card
 *    surface stays white in all three; tone lives in the numeral, bar and pill.
 */

export type MeterTone = 'normal' | 'warning' | 'spent'

const TONE_COLOR: Record<MeterTone, string> = {
  normal: 'var(--careco-primary)',
  warning: 'var(--careco-status-inactive)',
  spent: 'var(--careco-error)',
}

/**
 * Cards stack as full-width rows, one per allowance, rather than sitting side
 * by side. Full width keeps Care plan audits and Supervision as matching
 * strips, with Request increase lined up at the same inset on every one.
 */
export const MeterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--careco-space-md, 16px);
  width: 100%;
  margin-bottom: var(--careco-space-md, 16px);
`

/* ---- card ---- */

/**
 * One allowance as a full-width row: the icon, label and figures on the left,
 * Request increase vertically centred on the right. Wraps below the 560px
 * content floor rather than compressing the bar or clipping the button.
 *
 * The surface stays white in every tone. A card that turns solid green or amber
 * to signal state makes the strongest thing on the page its own background —
 * two cards stacked then read as one being selected rather than as one
 * running low. §02 is satisfied without it: the pill carries the word, the
 * numeral and the bar carry the colour.
 */
export const MeterCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 20px;
  box-sizing: border-box;
  padding: 20px 22px;
  border-radius: var(--careco-rounded-lg, 20px);
  background: var(--careco-surface);
  border: 1px solid var(--careco-border);
  box-shadow: var(--careco-shadow-card, 0 2px 10px 2px rgba(110, 110, 110, 0.06));
  font-family: var(--careco-font, 'Lato', sans-serif);
`

/** The label/figures/hint stack — everything left of the row's action. */
export const MeterCardBody = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 320px;
  min-width: 0;
`

export const CardHead = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

/** Icon and title travel together. */
export const CardHeadLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`

/** Brand-tinted chip, matching the Monthly-limits sections. */
export const MeterIcon = styled.span`
  flex-shrink: 0;
  width: 42px;
  height: 42px;
  border-radius: var(--careco-rounded-md, 12px);
  background: var(--careco-primary-light);
  color: var(--careco-primary);
  display: inline-flex;
  align-items: center;
  justify-content: center;
`

/** The number is the point of the card, so it gets §03's stat-value size. */
export const StatRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-top: 18px;
`

export const StatValue = styled.strong<{ tone: MeterTone }>`
  font-size: 30px;
  font-weight: 700;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  color: ${({ tone }) => TONE_COLOR[tone]};
`

export const StatLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  line-height: 1.3;
  color: var(--careco-text-secondary);
`

/**
 * Sits at the bar's right end (see TrackRow) rather than on its own line below
 * it — one less line of height on the card, and the bar itself reads as
 * "how much is left", the number beside it as "the exact figures", at a
 * glance, on the same row.
 */
export const FootMeta = styled.span`
  flex-shrink: 0;
  white-space: nowrap;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  line-height: 1.3;
  font-variant-numeric: tabular-nums;
  color: var(--careco-text-muted);
`

/**
 * Bar and figures, side by side. The bar gives up the width FootMeta needs —
 * deliberately narrower than the full card now, not a leftover of sharing the
 * row — and unlimited allowances (no bar at all) render FootMeta inside
 * FootMetaBlock instead, which keeps its own top margin for that one case.
 */
export const TrackRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 14px;
`

export const FootMetaBlock = styled.div`
  margin-top: 16px;
`

/**
 * Holds the metric label.
 *
 * Free to shrink. The old 210px floor existed to line bars up across stacked
 * full-width rows; in a card grid it only pushed the pill out of the narrowest
 * column.
 */
export const MeterIdentity = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`

export const MeterLabel = styled.span`
  font-size: 15px;
  font-weight: 700;
  line-height: 1.3;
  color: var(--careco-text-primary);
`

/**
 * Shares TrackRow with FootMeta now rather than spanning the card alone —
 * `flex: 1 1 auto` gives FootMeta's fixed-width text the room it needs first,
 * so the bar is only as wide as what is left, not the full card width.
 */
export const MeterTrack = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  height: 8px;
  border-radius: var(--careco-rounded-pill, 999px);
  background: var(--careco-divider);
  overflow: hidden;
`

export const MeterFill = styled.div<{ ratio: number; tone: MeterTone }>`
  height: 100%;
  width: ${({ ratio }) => Math.round(ratio * 100)}%;
  border-radius: inherit;
  background: ${({ tone }) => TONE_COLOR[tone]};

  /* §03: motion only where it clarifies a state change, off when unwanted. */
  transition: width 200ms ease;
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

export const MeterReset = styled.span`
  font-size: 13px;
  font-weight: 500;
  line-height: 1.3;
  color: var(--careco-text-muted);
  white-space: nowrap;
`

/* §11 status-pill, error variant. Carries a word, never colour alone. */
export const SpentPill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 5px 14px;
  border-radius: var(--careco-rounded-pill, 999px);
  background: var(--careco-error-light);
  color: var(--careco-error);
  font-size: 13.5px;
  font-weight: 600;
  line-height: 1.3;
  white-space: nowrap;
`

/**
 * Kept for callers that still want a hard right-aligned action.
 *
 * The meter itself no longer uses it: pushing this button to the page edge put
 * it directly beneath the page-header's own right-aligned primary button, ~50px
 * apart, at a different height and width, with right edges that did not even
 * line up because the card's padding insets this one. Two competing right-hand
 * actions in a column read as a layout fault. Grouped with the numbers it
 * belongs to, it reads as part of the strip.
 */
export const MeterSpacer = styled.span`
  flex: 1 1 auto;
  min-width: 0;
`

/*
 * A statement body, not a ternary. Prettier strips the wrapping parens off a
 * multi-line conditional in a concise arrow, which then trips ESLint's
 * `no-confusing-arrow` — the two tools disagree and the file cannot satisfy
 * both. Returning from a block sidesteps the argument entirely.
 */
const requestHover = (spent?: boolean): string => {
  if (spent) return 'background: var(--careco-primary-hover);'
  return 'border-color: var(--careco-primary); color: var(--careco-primary);'
}

/*
 * Primary fill when the allowance is spent (the action to take now), otherwise a
 * quiet secondary button. A direct child of MeterCard, at the row's right
 * edge — the card's own `justify-content: space-between` places it, so it
 * needs no margin of its own to get there.
 */
export const RequestButton = styled.button<{ spent?: boolean }>`
  flex-shrink: 0;
  height: 38px;
  padding: 0 20px;
  border-radius: var(--careco-rounded-sm, 8px);
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
  cursor: pointer;
  white-space: nowrap;
  border: 1px solid ${({ spent }) => (spent ? 'transparent' : 'var(--careco-border)')};
  background: ${({ spent }) => (spent ? 'var(--careco-primary)' : 'var(--careco-surface)')};
  color: ${({ spent }) => (spent ? '#ffffff' : 'var(--careco-text-primary)')};

  &:hover:not(:disabled) {
    ${({ spent }) => requestHover(spent)}
  }

  &:focus-visible {
    outline: 2px solid var(--careco-primary);
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`

/* ---- Supervision card ---- */

/**
 * The Supervision card reads top-to-bottom rather than left-to-right like the
 * metered cards — a header row, an intro line, then the two scheduling modes
 * laid out for comparison — so it takes the same MeterCard surface (colour,
 * radius, border, shadow) but overrides the row layout with a column one.
 */
export const SupervisionCard = styled(MeterCard)`
  /*
   * MeterCard's own \`gap: 20px\` is for its row layout (figures vs. the
   * Request button) and would otherwise stack with each child's own margin
   * below, doubling the space between the header, the intro line and the
   * option grid — which read as three loosely related blocks rather than
   * one card. Spacing between children here comes from their own margins.
   */
  flex-direction: column;
  align-items: stretch;
  gap: 0;
`

export const SupervisionHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`

export const SupervisionIntro = styled.p`
  margin: 8px 0 16px;
  font-size: 14px;
  line-height: 1.5;
  color: var(--careco-text-secondary);
`

export const OptionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`

export const OptionCard = styled.div<{ $active: boolean }>`
  box-sizing: border-box;
  padding: 16px 18px;
  border-radius: var(--careco-rounded-md, 12px);
  border: 1px solid ${({ $active }) => ($active ? 'var(--careco-primary)' : 'var(--careco-border)')};
  background: ${({ $active }) => ($active ? 'var(--careco-primary-light)' : 'var(--careco-surface)')};
`

export const OptionHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`

export const OptionTitle = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: var(--careco-text-primary);
`

/** Display-only checkbox — cards are read-only; mode changes via the request modal. */
export const OptionCheckbox = styled.span<{ $checked: boolean }>`
  box-sizing: border-box;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border-radius: 4px;
  border: 2px solid ${({ $checked }) => ($checked ? 'var(--careco-primary)' : 'var(--careco-border)')};
  background: ${({ $checked }) => ($checked ? 'var(--careco-primary)' : 'var(--careco-surface)')};
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: '';
    width: 10px;
    height: 6px;
    margin-top: -2px;
    border-left: 2px solid var(--careco-surface);
    border-bottom: 2px solid var(--careco-surface);
    transform: rotate(-45deg);
    opacity: ${({ $checked }) => ($checked ? 1 : 0)};
  }
`

export const OptionDesc = styled.p`
  margin: 8px 0 0;
  font-size: 13.5px;
  line-height: 1.5;
  color: var(--careco-text-secondary);
`

/* ---- Feature not-included card ---- */

/**
 * The supporting line under the label when Care Plan Audit or Supervision is
 * not part of the organisation's package. Sits in MeterCard's left-hand stack,
 * with the request action on the row's right.
 */
export const UnavailableText = styled.p`
  margin: 8px 0 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.4;
  color: var(--careco-text-secondary);
`

/* ---- Request-increase modal ---- */

export const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--careco-space-md, 16px);
  font-family: var(--careco-font, 'Lato', sans-serif);
`

/* §05 {typography.card-title} — 20px/600. */
export const ModalTitle = styled.h3`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.2;
  color: var(--careco-text-primary);
`

export const ModalText = styled.p`
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.4;
  color: var(--careco-text-secondary);
`

export const FieldLabel = styled.label`
  display: flex;
  flex-direction: column;
  gap: var(--careco-space-xs, 8px);
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
  color: var(--careco-text-primary);
`

/* §11 input/dropdown — 48px, {rounded.sm}, hairline {colors.border}. */
export const NumberInput = styled.input`
  box-sizing: border-box;
  width: 160px;
  height: 48px;
  padding: 0 14px;
  border: 1px solid var(--careco-border);
  border-radius: var(--careco-rounded-sm, 8px);
  font-family: inherit;
  font-size: 14px;
  font-weight: 400;
  color: var(--careco-text-primary);
  background: var(--careco-surface);

  &:focus-visible {
    outline: 2px solid var(--careco-primary);
    outline-offset: -1px;
  }
`

export const FieldError = styled.span`
  font-size: 12.5px;
  font-weight: 500;
  line-height: 1.35;
  color: var(--careco-error);
`

/**
 * The request modal's "New supervision mode" field — a dropdown, same as the
 * mode picker this whole flow replaced on the card itself, just full-width to
 * match the modal's other fields (NumberInput, TextArea) instead of the
 * fixed 200px it used sitting inline in a MeterCard row.
 */
export const ModalModeSelect = styled.select`
  box-sizing: border-box;
  width: 100%;
  height: 48px;
  padding: 0 36px 0 14px;
  border: 1px solid var(--careco-border);
  border-radius: var(--careco-rounded-sm, 8px);
  background: var(--careco-surface);
  font-family: inherit;
  font-size: 14px;
  font-weight: 400;
  color: var(--careco-text-primary);
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8' fill='none'><path d='M1 1.5L6 6.5L11 1.5' stroke='%23616581' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'/></svg>");
  background-repeat: no-repeat;
  background-position: right 14px center;

  &:focus-visible {
    outline: 2px solid var(--careco-primary);
    outline-offset: -1px;
  }
`

export const TextArea = styled.textarea`
  /*
   * These four resets are load-bearing, not defensive noise.
   * uiKit/inputs/Params.css is a plain (non-module) stylesheet with a bare
   * textarea selector setting position: absolute, height: 140px and z-index: 2.
   * Importing it anywhere leaks those onto every textarea in the app, which took
   * this one out of layout entirely — it painted on top of the modal's buttons
   * and forced a horizontal scrollbar. Remove these only once that selector is
   * scoped.
   */
  position: relative;
  z-index: auto;
  height: auto;
  display: block;

  box-sizing: border-box;
  width: 100%;
  min-height: 88px;
  resize: vertical;
  padding: 12px 14px;
  border: 1px solid var(--careco-border);
  border-radius: var(--careco-rounded-sm, 8px);
  font-family: inherit;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.4;
  color: var(--careco-text-primary);
  background: var(--careco-surface);

  &:focus-visible {
    outline: 2px solid var(--careco-primary);
    outline-offset: -1px;
  }
`

export const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: var(--careco-space-sm, 12px);
  margin-top: var(--careco-space-xxs, 4px);
`

/* §11 button-primary — 38px, {colors.primary}, {rounded.sm}. */
export const PrimaryButton = styled.button`
  height: 38px;
  padding: 0 18px;
  border: none;
  border-radius: var(--careco-rounded-sm, 8px);
  background: var(--careco-primary);
  color: #ffffff;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: var(--careco-primary-hover);
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`

/* §11 button-secondary. */
export const GhostButton = styled.button`
  height: 38px;
  padding: 0 18px;
  border: 1px solid var(--careco-border);
  border-radius: var(--careco-rounded-sm, 8px);
  background: var(--careco-surface);
  color: var(--careco-text-primary);
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: var(--careco-primary);
    color: var(--careco-primary);
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`
